import type { Candidate } from '../candidate'
import type { DerivedFacts } from '../facts'
import { RULE_IDS } from '../version'
import { evidenceFor, missingEvidenceFor, watchItemsFor } from './shared'

/**
 * Short aesthetic preparation (phase-system.md, rules-engine.md).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  EXTREME METHODS ARE REJECTED EXPLICITLY, NOT MERELY OMITTED.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This is the phase where a user is most likely to have heard about dehydration,
 * water loading, salt cutting, diuretics or a crash diet before the event, and
 * `testing-plan.md` requires the engine to **reject them by name** rather than
 * to stay silent. Silence reads as "the app has not thought about it"; naming
 * them is what makes the refusal informative. Every candidate here therefore
 * carries a rejected alternative that says so.
 *
 * Progress is judged against the event, but health and recovery come first, and
 * no rule in this family proposes a larger deficit — the approved range is
 * −0.5% to −0.3% (D-065), gentler than ordinary fat loss on purpose.
 */

/** Named so the refusal is specific rather than a vague caution. */
const EXTREME_METHODS_REJECTED = {
  action: 'Dehydrate, water-load, cut salt hard, use diuretics, or crash diet before the date',
  reason:
    'Project Sola never recommends any of these. They risk health, they cost strength and fullness on the day, and they make you look flatter rather than leaner.',
} as const

export function evaluateAestheticPrep(facts: DerivedFacts): Candidate {
  const phase = facts.phase
  if (!phase || phase.ratePosition === undefined) {
    throw new Error('aesthetic preparation rules require a phase and a comparable week')
  }

  if (phase.ratePosition === 'fasterThanRange') return lossFasterThanPlanned(facts)
  if (phase.ratePosition === 'insideRange') return onTrackForTheDate(facts)
  return behindThePlannedRate(facts)
}

function onTrackForTheDate(facts: DerivedFacts): Candidate {
  return {
    category: 'stayTheCourse',
    primaryAction:
      'Keep everything exactly as it is. You are on the rate you planned for the date, and consistency from here matters more than anything you could add.',
    supportingActions: [
      'Keep sleep, salt and food volume steady rather than changing them near the date.',
    ],
    ruleId: RULE_IDS.aestheticPrepOnTrack,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Presentation on the day comes from fullness and consistency, not from last-minute changes.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [EXTREME_METHODS_REJECTED],
    reducesIntakeOrActivity: false,
  }
}

function lossFasterThanPlanned(facts: DerivedFacts): Candidate {
  return {
    category: 'easeTheCurrentPhase',
    primaryAction:
      'Ease the deficit back to the rate you planned. Losing faster than this before an event costs muscle fullness, which is most of what being lean actually looks like.',
    supportingActions: ['Keep protein and sleep up while the rate settles.'],
    ruleId: RULE_IDS.aestheticPrepTooFast,
    family: 'safetyAndRecovery',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Fast loss close to a date usually shows up as looking flat rather than lean.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      EXTREME_METHODS_REJECTED,
      {
        action: 'Keep the faster rate to be certain of being lean enough',
        reason:
          'Being lean and looking good on the day are not the same thing. Rushing the last of it usually costs the second.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Behind the planned rate with a date approaching.
 *
 * **This deliberately does not tell the user to cut harder.** The obvious
 * response is exactly the one this phase exists to prevent, so the app states
 * the trade-off and leaves the decision where it belongs — with the user, who
 * can move the target rather than their health.
 */
function behindThePlannedRate(facts: DerivedFacts): Candidate {
  const phase = facts.phase

  return {
    category: 'doNotAdjustYet',
    primaryAction:
      'Hold the current plan rather than tightening it. You are behind the rate you planned, and the safe options are to accept a slightly softer result or to adjust what you are aiming for — not to cut harder as the date closes in.',
    supportingActions: [],
    ruleId: RULE_IDS.aestheticPrepBehind,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      phase === undefined
        ? 'Consistency matters more than rate this close to a date.'
        : `${Math.max(phase.daysToPlannedEnd, 0)} days remain in this phase. Consistency from here matters more than rate.`,
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      EXTREME_METHODS_REJECTED,
      {
        action: 'Increase the deficit to make up the difference before the date',
        reason:
          'Cutting harder as an event approaches is where crash dieting starts. The rate for this phase was set gently on purpose.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
